import { useState } from 'react';
import { Landmark, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  useBankAccounts,
  useCreateBankAccount,
  useUpdateBankAccount,
  useDeleteBankAccount,
} from '../../hooks/useBankAccounts';
import { useCompanies } from '../../hooks/useCompanies';
import type {
  BankAccount,
  BankAccountInput,
  BankAccountUpdateInput,
} from '../../lib/bankAccountsApi';
import { useConfirm } from '../ui/ConfirmProvider';

// Settings tab: "Conturi bancare". Lists bank accounts grouped by company,
// supports add/edit/deactivate. Gated on settings.canManageCompanies.

interface FormState {
  iban: string;
  bankName: string;
  label: string;
  currency: 'RON' | 'EUR' | 'USD';
  companyId: string;
}

const EMPTY_FORM: FormState = { iban: '', bankName: '', label: '', currency: 'RON', companyId: '' };

function accountToForm(a: BankAccount): FormState {
  return {
    iban: a.iban,
    bankName: a.bankName,
    label: a.label ?? '',
    currency: a.currency as 'RON' | 'EUR' | 'USD',
    companyId: a.companyId ?? '',
  };
}

export function BankAccountsSettings() {
  const { data: accounts, isLoading, isError } = useBankAccounts();
  const { data: companies } = useCompanies();
  const deleteMutation = useDeleteBankAccount();
  const updateMutation = useUpdateBankAccount();
  const confirm = useConfirm();

  const [modal, setModal] = useState<'new' | BankAccount | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function handleToggleActive(account: BankAccount) {
    setError(null);
    try {
      await updateMutation.mutateAsync({
        id: account.id,
        input: { isActive: !account.isActive },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Eroare');
    }
  }

  async function handleDelete(account: BankAccount) {
    setError(null);
    setInfo(null);
    if (!(await confirm({ message: `Sigur vrei sa stergi contul ${account.iban}?`, danger: true })))
      return;
    try {
      const result = await deleteMutation.mutateAsync(account.id);
      if (result.deactivated) {
        setInfo(result.reason ?? 'Contul a fost dezactivat.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Eroare');
    }
  }

  // Group accounts by company name for display.
  const grouped = new Map<string, BankAccount[]>();
  if (accounts) {
    for (const a of accounts) {
      const key = a.company?.name ?? 'Firma principala';
      const list = grouped.get(key) ?? [];
      list.push(a);
      grouped.set(key, list);
    }
  }

  return (
    <div className="max-w-3xl">
      {/* Phones: the explanation on top, the action full width under it
          (squeezed beside the text it wrapped the text to a narrow column). */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&>button]:justify-center">
        <div>
          <p className="text-sm text-gray-500">
            Gestioneaza conturile bancare ale firmelor. Acestea sunt folosite pentru detectia
            automata a transferurilor interne si auto-linkarea extraselor bancare.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal('new')}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          <Plus className="h-4 w-4" />
          Adauga cont
        </button>
      </div>

      {error && (
        <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
          {error}
        </p>
      )}
      {info && (
        <p className="mt-4 rounded-md border border-blue-500/30 bg-blue-500/10 p-3 text-sm text-blue-600">
          {info}
        </p>
      )}

      {isLoading && <p className="mt-6 text-sm text-gray-500">Se incarca...</p>}
      {isError && <p className="mt-6 text-sm text-red-600">Nu am putut incarca conturile.</p>}

      {accounts && accounts.length === 0 && (
        <p className="mt-6 text-sm text-gray-500">
          Niciun cont bancar inregistrat. Adauga primul cont pentru a activa detectia transferurilor
          interne.
        </p>
      )}

      {accounts && accounts.length > 0 && (
        <div className="mt-6 space-y-6">
          {[...grouped.entries()].map(([companyName, accts]) => (
            <div key={companyName}>
              <h3 className="mb-2 text-sm font-semibold text-gray-700">{companyName}</h3>
              <div className="space-y-2">
                {accts.map((a) => (
                  <div
                    key={a.id}
                    // Phones: account details on top, the actions on a row of
                    // their own (beside them the IBAN broke mid-group and the
                    // toggle label was cut to "Dezactiv.").
                    className={`flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between ${
                      a.isActive
                        ? 'border-gray-200 bg-white'
                        : 'border-gray-100 bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <Landmark className="h-5 w-5 shrink-0 text-gray-400" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          <span className="whitespace-nowrap">{formatIban(a.iban)}</span>
                          {a.label && (
                            <span className="block text-xs text-gray-500 sm:ml-2 sm:inline">
                              ({a.label})
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {a.bankName} &middot; {a.currency}
                          {!a.isActive && (
                            <span className="ml-1 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                              INACTIV
                            </span>
                          )}
                          {a.statementsCount > 0 && (
                            <span className="ml-1 text-gray-400">
                              &middot; {a.statementsCount} extrase
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(a)}
                        className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        title={a.isActive ? 'Dezactiveaza' : 'Reactiveaza'}
                      >
                        <span className="text-xs">
                          <span className="sm:hidden">
                            {a.isActive ? 'Dezactivează' : 'Reactivează'}
                          </span>
                          <span className="hidden sm:inline">
                            {a.isActive ? 'Dezactiv.' : 'Activ.'}
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setModal(a)}
                        className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        title="Editeaza"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(a)}
                        className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-500"
                        title="Sterge"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <BankAccountModal
          mode={modal === 'new' ? 'create' : 'edit'}
          account={modal === 'new' ? undefined : modal}
          companies={companies ?? []}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

function BankAccountModal({
  mode,
  account,
  companies,
  onClose,
}: {
  mode: 'create' | 'edit';
  account?: BankAccount;
  companies: Array<{
    id: string;
    legalName: string;
    displayName: string | null;
    isDefault: boolean;
  }>;
  onClose: () => void;
}) {
  const createMutation = useCreateBankAccount();
  const updateMutation = useUpdateBankAccount();
  const [form, setForm] = useState<FormState>(account ? accountToForm(account) : EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  function update(patch: Partial<FormState>) {
    setForm((prev) => ({ ...prev, ...patch }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (mode === 'create') {
        const input: BankAccountInput = {
          iban: form.iban.trim(),
          bankName: form.bankName.trim(),
          label: form.label.trim() || null,
          currency: form.currency,
          companyId: form.companyId || null,
        };
        await createMutation.mutateAsync(input);
      } else if (account) {
        const input: BankAccountUpdateInput = {
          iban: form.iban.trim(),
          bankName: form.bankName.trim(),
          label: form.label.trim() || null,
          currency: form.currency,
        };
        await updateMutation.mutateAsync({ id: account.id, input });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Eroare la salvare');
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === 'create' ? 'Cont bancar nou' : 'Editeaza cont'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">IBAN</label>
            <input
              type="text"
              value={form.iban}
              onChange={(e) => update({ iban: e.target.value })}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="RO49AAAA1B31007593840000"
              required
              minLength={5}
              disabled={mode === 'edit'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Banca</label>
            <input
              type="text"
              value={form.bankName}
              onChange={(e) => update({ bankName: e.target.value })}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="ING Bank"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Denumire cont (optional)
            </label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => update({ label: e.target.value })}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="Cont de incasari"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Moneda</label>
              <select
                value={form.currency}
                onChange={(e) => update({ currency: e.target.value as 'RON' | 'EUR' | 'USD' })}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="RON">RON</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </div>

            {mode === 'create' && companies.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Firma</label>
                <select
                  value={form.companyId}
                  onChange={(e) => update({ companyId: e.target.value })}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                >
                  <option value="">Firma principala</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.displayName || c.legalName}
                      {c.isDefault ? ' (principala)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {error && (
            <p className="rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-red-600">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Anuleaza
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="inline-flex items-center rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isPending ? 'Se salveaza...' : mode === 'create' ? 'Adauga' : 'Salveaza'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Format IBAN in groups of 4 for readability. */
function formatIban(iban: string): string {
  return iban
    .replace(/\s+/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim();
}
